import 'maplibre-gl/dist/maplibre-gl.css'
import type { Feature, FeatureCollection } from 'geojson'
import maplibregl from 'maplibre-gl'
import { Protocol } from 'pmtiles'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { stormSlug } from '../lib/disruptions-api'
import {
  type Cyclone,
  type OceanStorm,
  cycloneCenterAt,
  galeKmFor,
  intensityStyle,
  oceanGaleKmFor,
  oceanStormCenterAt,
} from '../lib/weather-api'
export interface MapPort {
  code: string
  name: string
  lat: number
  lon: number
  level?: 'severe' | 'moderate' | 'mild' | null
}

// ── Option B: a real MapLibre GL vector map, fully self-hosted (China-safe, CSP-safe) ──────────
//
// BASEMAP: Natural Earth country shapes + labels + Noto Sans glyphs, all served from our own
// /basemap/* (no external tile server, no key, no third-party requests). Navo-styled, light + dark.
// The pmtiles:// protocol is registered so a future self-hosted /basemap.pmtiles can add street-zoom
// without touching this component.
//
// OVERLAY: the live cyclones / congested ports. Trade lanes are NOT drawn as a decorative web —
// a red arc appears ONLY on the sub-segment where a storm's gale footprint actually crosses a route,
// so a line always means "this shipping route is disrupted right here."
maplibregl.addProtocol('pmtiles', new Protocol().tile)

type FC = FeatureCollection
const fc = (features: Feature[]): FC => ({ type: 'FeatureCollection', features })
const line = (coords: number[][], props: Record<string, unknown> = {}): Feature => ({
  type: 'Feature',
  properties: props,
  geometry: { type: 'LineString', coordinates: coords },
})
const point = (c: [number, number], props: Record<string, unknown> = {}): Feature => ({
  type: 'Feature',
  properties: props,
  geometry: { type: 'Point', coordinates: c },
})

// Geodesic circle (gale-radius footprint) as a polygon ring in [lng,lat].
function circle([lng, lat]: [number, number], km: number): Feature {
  const R = 6371
  const pts: number[][] = []
  for (let i = 0; i <= 72; i++) {
    const a = (i / 72) * 2 * Math.PI
    const dy = ((km / R) * Math.sin(a) * 180) / Math.PI
    const dx =
      ((km / R) * Math.cos(a) * 180) / Math.PI / Math.max(0.08, Math.cos((lat * Math.PI) / 180))
    pts.push([lng + dx, lat + dy])
  }
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [pts] } }
}

// Catmull-Rom spline → a smooth curve through the track points (no jagged polyline).
function smooth(pts: number[][], steps = 14): number[][] {
  if (pts.length < 3) return pts
  const out: number[][] = []
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[i + 2] ?? p2
    if (!p0 || !p1 || !p2 || !p3) continue
    const [x0, y0] = p0 as [number, number]
    const [x1, y1] = p1 as [number, number]
    const [x2, y2] = p2 as [number, number]
    const [x3, y3] = p3 as [number, number]
    for (let t = 0; t < steps; t++) {
      const s = t / steps
      const s2 = s * s
      const s3 = s2 * s
      const x =
        0.5 *
        (2 * x1 +
          (-x0 + x2) * s +
          (2 * x0 - 5 * x1 + 4 * x2 - x3) * s2 +
          (-x0 + 3 * x1 - 3 * x2 + x3) * s3)
      const y =
        0.5 *
        (2 * y1 +
          (-y0 + y2) * s +
          (2 * y0 - 5 * y1 + 4 * y2 - y3) * s2 +
          (-y0 + 3 * y1 - 3 * y2 + y3) * s3)
      out.push([x, y])
    }
  }
  const last = pts[pts.length - 1]
  if (last) out.push(last)
  return out
}

// Resolve a CSS var() color string to a concrete value MapLibre (WebGL) can use.
function resolve(color: string, fallback: string): string {
  if (!color.startsWith('var(')) return color
  const name = (color.slice(4, -1).split(',')[0] ?? '').trim()
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

interface Palette {
  ocean: string
  land: string
  border: string
  grat: string
  label: string
  labelHalo: string
  sea: string
  hot: string
  ports: { severe: string; moderate: string; mild: string; none: string }
  eyeStroke: string
}
function readPalette(dark: boolean): Palette {
  return {
    ocean: dark ? '#0a0e16' : '#dbe4f1',
    land: dark ? '#161d2c' : '#f4f4f1',
    border: dark ? 'rgba(255,255,255,0.10)' : '#cdd5e1',
    grat: dark ? 'rgba(74,134,255,0.10)' : 'rgba(31,79,230,0.07)',
    label: dark ? '#9aa6b6' : '#5c6675',
    labelHalo: dark ? '#0a0e16' : '#f4f4f1',
    sea: dark ? 'rgba(150,170,200,0.55)' : '#9fb0c6',
    hot: resolve('var(--danger)', dark ? '#ff5b5b' : '#e2231a'),
    ports: {
      severe: resolve('var(--danger)', dark ? '#ff5b5b' : '#e2231a'),
      moderate: resolve('var(--warn)', dark ? '#ffb020' : '#d98600'),
      mild: resolve('var(--signal)', dark ? '#4a86ff' : '#1f4fe6'),
      none: resolve('var(--signal)', dark ? '#4a86ff' : '#1f4fe6'),
    },
    eyeStroke: dark ? '#0a0e16' : '#ffffff',
  }
}

// Graticule every 20°, subdivided so lines curve correctly under web-mercator.
function graticule(): FC {
  const lines: Feature[] = []
  for (let lng = -180; lng <= 180; lng += 20) {
    const pts: number[][] = []
    for (let lat = -80; lat <= 80; lat += 5) pts.push([lng, lat])
    lines.push(line(pts))
  }
  for (let lat = -80; lat <= 80; lat += 20) {
    const pts: number[][] = []
    for (let lng = -180; lng <= 180; lng += 5) pts.push([lng, lat])
    lines.push(line(pts))
  }
  return fc(lines)
}

interface Props {
  cyclones: Cyclone[]
  ports: MapPort[]
  oceanStorms?: OceanStorm[]
  showScrubber?: boolean
  /** Auto-frame the viewport to the storm/port data (for single-storm pages). */
  fit?: boolean
  className?: string
}

export function WeatherMapGL({
  cyclones,
  ports,
  oceanStorms = [],
  showScrubber = false,
  fit = false,
  className,
}: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const readyRef = useRef(false)
  const applyRef = useRef<() => void>(() => {})
  const navigate = useNavigate()
  const [hours, setHours] = useState(0)
  const [dark, setDark] = useState(
    () => (document.documentElement.getAttribute('data-theme') ?? 'light') === 'dark'
  )

  // Track theme flips so basemap + overlay colors follow the site toggle.
  useEffect(() => {
    const el = document.documentElement
    const obs = new MutationObserver(() =>
      setDark((el.getAttribute('data-theme') ?? 'light') === 'dark')
    )
    obs.observe(el, { attributes: true, attributeFilter: ['data-theme'] })
    return () => obs.disconnect()
  }, [])

  // Build overlay GeoJSON from the live data at forecast hour `hours`. The map shows the STORMS
  // (glowing eye, gale halo, smooth observed + forecast track) and the congested PORTS — no lane
  // geometry (which route is exposed is answered by the list beside the map, in words).
  function buildOverlays(pal: Palette) {
    const gale: Feature[] = []
    const trackHist: Feature[] = []
    const trackFcst: Feature[] = []
    const fcstNodes: Feature[] = []
    const eyes: Feature[] = []
    for (const c of cyclones) {
      const color = resolve(intensityStyle(c.category).color, pal.hot)
      const now = cycloneCenterAt(c, hours)
      const center: [number, number] = [now.lon, now.lat]
      const km = galeKmFor(c)
      if (km > 0) gale.push({ ...circle(center, km), properties: { color } })
      const origin: [number, number] = [c.center.lon, c.center.lat]
      const hist = c.track.filter((p) => p.kind === 'history').map((p) => [p.lon, p.lat])
      const fcst = c.track.filter((p) => p.kind === 'forecast').map((p) => [p.lon, p.lat])
      if (hist.length) trackHist.push(line(smooth([...hist, origin]), { color }))
      if (fcst.length) {
        trackFcst.push(line(smooth([origin, ...fcst]), { color }))
        for (const p of fcst) fcstNodes.push(point(p as [number, number], { color }))
      }
      eyes.push(point(center, { color, slug: stormSlug(c.id), name: c.name }))
    }
    for (const s of oceanStorms) {
      const color = resolve(intensityStyle(null).color, pal.hot)
      const now = oceanStormCenterAt(s, hours)
      const km = oceanGaleKmFor(s)
      if (km > 0) gale.push({ ...circle([now.lon, now.lat], km), properties: { color: pal.hot } })
      eyes.push(point([now.lon, now.lat], { color, name: s.basin }))
    }

    const portFeats = ports
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon))
      .map((p) =>
        point([p.lon, p.lat], {
          code: p.code,
          name: p.name,
          color: p.level ? pal.ports[p.level] : pal.ports.none,
          r:
            p.level === 'severe'
              ? 5.5
              : p.level === 'moderate'
                ? 5
                : p.level === 'mild'
                  ? 4.5
                  : 2.6,
          congested: p.level ? 1 : 0,
        })
      )

    return {
      gale: fc(gale),
      trackHist: fc(trackHist),
      trackFcst: fc(trackFcst),
      fcstNodes: fc(fcstNodes),
      eyes: fc(eyes),
      ports: fc(portFeats),
    }
  }

  // Create the map once. Basemap layers are added on style load; overlays are filled by the data
  // effect below.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is created once; data/theme handled separately.
  useEffect(() => {
    if (!ref.current) return
    const pal = readPalette(dark)
    const map = new maplibregl.Map({
      container: ref.current,
      center: [140, 20],
      zoom: 1.5,
      minZoom: 0.7,
      maxZoom: 6,
      renderWorldCopies: true,
      attributionControl: false,
      style: {
        version: 8,
        glyphs: '/basemap/fonts/{fontstack}/{range}.pbf',
        sources: {},
        layers: [{ id: 'bg', type: 'background', paint: { 'background-color': pal.ocean } }],
      },
    })
    mapRef.current = map
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    map.addControl(
      new maplibregl.AttributionControl({
        compact: false,
        customAttribution: 'Natural Earth',
      }),
      'bottom-right'
    )

    map.on('load', () => {
      // ── Basemap sources (self-hosted, same-origin) ──
      map.addSource('countries', { type: 'geojson', data: '/basemap/countries.geojson' })
      map.addSource('clabels', { type: 'geojson', data: '/basemap/country-labels.geojson' })
      map.addSource('slabels', { type: 'geojson', data: '/basemap/ocean-labels.geojson' })
      map.addSource('grat', { type: 'geojson', data: graticule() })

      map.addLayer({
        id: 'land',
        type: 'fill',
        source: 'countries',
        paint: { 'fill-color': pal.land },
      })
      map.addLayer({
        id: 'grat',
        type: 'line',
        source: 'grat',
        paint: { 'line-color': pal.grat, 'line-width': 0.6 },
      })
      map.addLayer({
        id: 'border',
        type: 'line',
        source: 'countries',
        paint: {
          'line-color': pal.border,
          'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.4, 5, 1],
        },
      })

      // Overlay sources (empty until the data effect runs)
      // ── Place/ocean labels sit BENEATH the storm & port markers ──
      map.addLayer({
        id: 'slabels',
        type: 'symbol',
        source: 'slabels',
        layout: {
          'text-field': ['get', 'n'],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 1, 9, 5, 13],
          'text-letter-spacing': 0.18,
          'text-transform': 'uppercase',
          'text-max-width': 6,
        },
        paint: { 'text-color': pal.sea, 'text-halo-color': pal.ocean, 'text-halo-width': 0.6 },
      })
      map.addLayer({
        id: 'clabels',
        type: 'symbol',
        source: 'clabels',
        layout: {
          'text-field': ['get', 'n'],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 1, 9.5, 5, 15],
          'text-max-width': 7,
          'symbol-sort-key': ['get', 'r'],
        },
        paint: {
          'text-color': pal.label,
          'text-halo-color': pal.labelHalo,
          'text-halo-width': 1.1,
        },
      })

      const empty = fc([])
      for (const id of ['gale', 'trackHist', 'trackFcst', 'fcstNodes', 'eyes', 'ports'])
        map.addSource(id, { type: 'geojson', data: empty })

      // ── Gale footprint: a soft wash + a crisp dashed boundary ──
      map.addLayer({
        id: 'galeFill',
        type: 'fill',
        source: 'gale',
        paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.06 },
      })
      map.addLayer({
        id: 'galeLine',
        type: 'line',
        source: 'gale',
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 1.2,
          'line-dasharray': [2.5, 2.5],
          'line-opacity': 0.65,
        },
      })
      // ── Track: smoothed observed (solid) → forecast (dashed) + timestep nodes ──
      map.addLayer({
        id: 'trackHist',
        type: 'line',
        source: 'trackHist',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ['get', 'color'], 'line-width': 2.4, 'line-opacity': 0.9 },
      })
      map.addLayer({
        id: 'trackFcst',
        type: 'line',
        source: 'trackFcst',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 1.9,
          'line-dasharray': [1.5, 2],
          'line-opacity': 0.8,
        },
      })
      map.addLayer({
        id: 'fcstNodes',
        type: 'circle',
        source: 'fcstNodes',
        paint: {
          'circle-radius': 2.4,
          'circle-color': pal.ocean,
          'circle-stroke-color': ['get', 'color'],
          'circle-stroke-width': 1.4,
          'circle-opacity': 0.95,
        },
      })
      // ── Ports: soft congestion halo behind a crisp dot ──
      map.addLayer({
        id: 'portsHalo',
        type: 'circle',
        source: 'ports',
        filter: ['==', ['get', 'congested'], 1],
        paint: {
          'circle-radius': ['*', ['get', 'r'], 2.6],
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.2,
          'circle-blur': 0.7,
        },
      })
      map.addLayer({
        id: 'ports',
        type: 'circle',
        source: 'ports',
        paint: {
          'circle-radius': ['get', 'r'],
          'circle-color': ['get', 'color'],
          'circle-stroke-color': pal.eyeStroke,
          'circle-stroke-width': ['case', ['==', ['get', 'congested'], 1], 1.6, 0.8],
          'circle-opacity': ['case', ['==', ['get', 'congested'], 1], 1, 0.55],
        },
      })
      // ── Storm eye: layered glow → halo → white-ringed core (a designed cyclone marker) ──
      map.addLayer({
        id: 'eyeGlow',
        type: 'circle',
        source: 'eyes',
        paint: {
          'circle-radius': 22,
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.16,
          'circle-blur': 1,
        },
      })
      map.addLayer({
        id: 'eyeHalo',
        type: 'circle',
        source: 'eyes',
        paint: {
          'circle-radius': 12,
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.3,
          'circle-blur': 0.5,
        },
      })
      map.addLayer({
        id: 'eyeDot',
        type: 'circle',
        source: 'eyes',
        paint: {
          'circle-radius': 6.5,
          'circle-color': ['get', 'color'],
          'circle-stroke-color': pal.eyeStroke,
          'circle-stroke-width': 2,
        },
      })
      map.addLayer({
        id: 'eyeCore',
        type: 'circle',
        source: 'eyes',
        paint: { 'circle-radius': 2.2, 'circle-color': pal.eyeStroke },
      })

      // Interactions: click a congested port or a storm eye to open its page.
      const pointer = (on: boolean) => {
        map.getCanvas().style.cursor = on ? 'pointer' : ''
      }
      for (const layer of ['ports', 'eyeDot']) {
        map.on('mouseenter', layer, () => pointer(true))
        map.on('mouseleave', layer, () => pointer(false))
      }
      map.on('click', 'ports', (e) => {
        const code = e.features?.[0]?.properties?.code
        if (code) navigate(`/ports/${code}`)
      })
      map.on('click', 'eyeDot', (e) => {
        const slug = e.features?.[0]?.properties?.slug
        if (slug) navigate(`/storms/${slug}`)
      })

      readyRef.current = true
      applyRef.current()
    })

    return () => {
      readyRef.current = false
      mapRef.current = null
      map.remove()
    }
  }, [])

  // Push data + palette into the map whenever the live data, forecast hour, or theme changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: buildOverlays/fit are stable module helpers; re-running would needlessly re-render the map
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const pal = readPalette(dark)
    const apply = () => {
      const o = buildOverlays(pal)
      ;(map.getSource('gale') as maplibregl.GeoJSONSource)?.setData(o.gale)
      ;(map.getSource('trackHist') as maplibregl.GeoJSONSource)?.setData(o.trackHist)
      ;(map.getSource('trackFcst') as maplibregl.GeoJSONSource)?.setData(o.trackFcst)
      ;(map.getSource('fcstNodes') as maplibregl.GeoJSONSource)?.setData(o.fcstNodes)
      ;(map.getSource('eyes') as maplibregl.GeoJSONSource)?.setData(o.eyes)
      ;(map.getSource('ports') as maplibregl.GeoJSONSource)?.setData(o.ports)
      // Basemap + marker recolor (theme)
      map.setPaintProperty('bg', 'background-color', pal.ocean)
      const set = (id: string, prop: string, v: unknown) => {
        if (map.getLayer(id)) map.setPaintProperty(id, prop, v as never)
      }
      set('land', 'fill-color', pal.land)
      set('border', 'line-color', pal.border)
      set('grat', 'line-color', pal.grat)
      set('clabels', 'text-color', pal.label)
      set('clabels', 'text-halo-color', pal.labelHalo)
      set('slabels', 'text-color', pal.sea)
      set('slabels', 'text-halo-color', pal.ocean)
      set('fcstNodes', 'circle-color', pal.ocean)
      set('ports', 'circle-stroke-color', pal.eyeStroke)
      set('eyeDot', 'circle-stroke-color', pal.eyeStroke)
      set('eyeCore', 'circle-color', pal.eyeStroke)

      // Auto-frame to the storm/port data (single-storm pages).
      if (fit) {
        const b = new maplibregl.LngLatBounds()
        let any = false
        for (const c of cyclones) {
          const km = galeKmFor(c)
          const dLat = km / 111
          const dLon = km / (111 * Math.max(0.08, Math.cos((c.center.lat * Math.PI) / 180)))
          b.extend([c.center.lon - dLon, c.center.lat - dLat])
          b.extend([c.center.lon + dLon, c.center.lat + dLat])
          for (const p of c.track) b.extend([p.lon, p.lat])
          any = true
        }
        for (const p of ports) {
          if (Number.isFinite(p.lat) && Number.isFinite(p.lon)) {
            b.extend([p.lon, p.lat])
            any = true
          }
        }
        if (any) map.fitBounds(b, { padding: 70, maxZoom: 5, duration: 0 })
      }
    }
    applyRef.current = apply
    if (readyRef.current) apply()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cyclones, ports, oceanStorms, hours, dark])

  return (
    <div className="relative wx-gl">
      <div
        ref={ref}
        className={
          className ?? 'w-full h-[540px] rounded-2xl overflow-hidden border border-border-strong'
        }
      />
      {showScrubber && (
        <div className="absolute bottom-4 left-4 right-16 sm:right-auto sm:w-96 rounded-xl border border-border-strong bg-[var(--surface-1)]/90 backdrop-blur px-4 py-3 shadow-lg">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-mono text-xs uppercase tracking-widest text-[var(--text-muted)]">
              Forecast
            </span>
            <span className="font-mono text-xs text-[var(--text-secondary)]">
              {hours === 0 ? 'now' : `+${hours}h`}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={72}
            step={6}
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="w-full accent-[var(--signal)]"
            aria-label="Forecast hour"
          />
        </div>
      )}
    </div>
  )
}
